import { useState, useEffect, useCallback, useRef } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Clock, UserCheck, Stethoscope, CheckCircle, CreditCard, Phone, X, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api';

const STATUS_CONFIG = {
  confirmed:   { label: 'Ожидается',  color: 'bg-blue-900/30 border-blue-800/50 text-blue-300',    icon: Clock,        badge: 'bg-blue-600' },
  waiting:     { label: 'В приёмной', color: 'bg-amber-900/30 border-amber-800/50 text-amber-300', icon: UserCheck,     badge: 'bg-amber-600' },
  in_progress: { label: 'На приёме',  color: 'bg-violet-900/30 border-violet-800/50 text-violet-300', icon: Stethoscope, badge: 'bg-violet-600' },
  completed:   { label: 'Завершён',   color: 'bg-emerald-900/30 border-emerald-800/50 text-emerald-300', icon: CheckCircle, badge: 'bg-emerald-600' },
  paid:        { label: 'Оплачен',    color: 'bg-stone-800/50 border-stone-700/50 text-stone-400',  icon: CreditCard,   badge: 'bg-stone-600' },
};

const NEXT_STATUS = {
  confirmed:   { status: 'waiting',     label: 'Пришёл',       icon: UserCheck },
  waiting:     { status: 'in_progress', label: 'Начать приём', icon: Stethoscope },
  in_progress: { status: 'completed',   label: 'Завершить',    icon: CheckCircle },
  completed:   { status: 'paid',        label: 'Оплачен',      icon: CreditCard },
};

export default function LiveQueuePage() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('doctor');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const prevStatusesRef = useRef({});
  const audioCtxRef = useRef(null);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      // Two-tone chime
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch {}
  }, [soundEnabled]);

  const fetchData = useCallback(async () => {
    try {
      const [meRes, apptRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/appointments'),
      ]);
      setRole(meRes.data.role || 'doctor');
      const today = new Date().toISOString().split('T')[0];
      const todayAppts = (Array.isArray(apptRes.data) ? apptRes.data : [])
        .filter(a => a.date === today && !['cancelled', 'pending'].includes(a.status))
        .sort((a, b) => a.time.localeCompare(b.time));

      // Detect status changes for sound notification
      const prev = prevStatusesRef.current;
      let hasChange = false;
      todayAppts.forEach(a => {
        if (prev[a.id] && prev[a.id] !== a.status) {
          hasChange = true;
        }
      });
      if (hasChange && Object.keys(prev).length > 0) {
        playNotificationSound();
      }
      // Update previous statuses
      const newStatuses = {};
      todayAppts.forEach(a => { newStatuses[a.id] = a.status; });
      prevStatusesRef.current = newStatuses;

      setAppointments(todayAppts);
    } catch (err) {
      toast.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [playNotificationSound]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const transitionStatus = async (apptId, newStatus) => {
    try {
      await api.put(`/appointments/${apptId}/status`, { status: newStatus });
      toast.success(`Статус обновлён: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const cancelAppt = async (apptId) => {
    try {
      await api.put(`/appointments/${apptId}/status`, { status: 'cancelled' });
      toast.success('Запись отменена');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const grouped = {
    waiting:     appointments.filter(a => a.status === 'waiting'),
    in_progress: appointments.filter(a => a.status === 'in_progress'),
    confirmed:   appointments.filter(a => a.status === 'confirmed'),
    completed:   appointments.filter(a => a.status === 'completed'),
    paid:        appointments.filter(a => a.status === 'paid'),
  };

  const stats = {
    total: appointments.length,
    waiting: grouped.waiting.length,
    in_progress: grouped.in_progress.length,
    completed: grouped.completed.length + grouped.paid.length,
    revenue: grouped.paid.reduce((sum, a) => sum + (a.price || 0), 0),
  };

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto" data-testid="live-queue-page">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--app-text)' }}>
                Живая очередь
              </h1>
              <p className="text-sm mt-1" style={{ color: 'var(--app-text-muted)' }}>
                {new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>
            <div className="flex gap-2 text-xs">
              <button data-testid="sound-toggle" onClick={() => setSoundEnabled(s => !s)}
                className={`px-3 py-1.5 rounded-full border transition-all ${soundEnabled ? 'bg-amber-900/20 text-amber-400 border-amber-800/40' : 'bg-stone-800/30 text-stone-600 border-stone-700/40'}`}>
                {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
              <span className="px-3 py-1.5 rounded-full bg-blue-900/30 text-blue-400 border border-blue-800/40">
                {stats.waiting} в приёмной
              </span>
              <span className="px-3 py-1.5 rounded-full bg-violet-900/30 text-violet-400 border border-violet-800/40">
                {stats.in_progress} на приёме
              </span>
              <span className="px-3 py-1.5 rounded-full bg-emerald-900/30 text-emerald-400 border border-emerald-800/40">
                {stats.completed} завершено
              </span>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20" style={{ color: 'var(--app-text-muted)' }}>Загрузка...</div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-20 rounded-xl border" style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}>
              <Clock size={48} className="mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Нет записей на сегодня</p>
              <p className="text-sm mt-1">Записи появятся здесь автоматически</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Active sections: waiting + in_progress */}
              {(grouped.waiting.length > 0 || grouped.in_progress.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* In progress - top priority */}
                  {grouped.in_progress.length > 0 && (
                    <div className="space-y-3">
                      <h2 className="text-sm font-semibold text-violet-400 uppercase tracking-wider">На приёме</h2>
                      {grouped.in_progress.map(a => (
                        <AppointmentCard key={a.id} appointment={a} role={role}
                          onTransition={transitionStatus} onCancel={cancelAppt} />
                      ))}
                    </div>
                  )}
                  {/* Waiting */}
                  {grouped.waiting.length > 0 && (
                    <div className="space-y-3">
                      <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">В приёмной</h2>
                      {grouped.waiting.map(a => (
                        <AppointmentCard key={a.id} appointment={a} role={role}
                          onTransition={transitionStatus} onCancel={cancelAppt} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Upcoming */}
              {grouped.confirmed.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Ожидаются сегодня</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {grouped.confirmed.map(a => (
                      <AppointmentCard key={a.id} appointment={a} role={role}
                        onTransition={transitionStatus} onCancel={cancelAppt} compact />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed + Paid */}
              {(grouped.completed.length > 0 || grouped.paid.length > 0) && (
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">Завершённые</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[...grouped.completed, ...grouped.paid].map(a => (
                      <AppointmentCard key={a.id} appointment={a} role={role}
                        onTransition={transitionStatus} onCancel={cancelAppt} compact />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function AppointmentCard({ appointment: a, role, onTransition, onCancel, compact = false }) {
  const config = STATUS_CONFIG[a.status] || STATUS_CONFIG.confirmed;
  const next = NEXT_STATUS[a.status];
  const Icon = config.icon;

  return (
    <div data-testid={`queue-card-${a.id}`}
      className={`rounded-xl border p-4 transition-all ${config.color} ${compact ? '' : 'shadow-lg'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white ${config.badge}`}>
              <Icon size={10} />
              {config.label}
            </span>
            <span className="text-sm font-mono opacity-70">{a.time}</span>
          </div>
          <p className="font-semibold text-base truncate" style={{ color: 'var(--app-text)' }}>
            {a.patient_name || 'Клиент'}
          </p>
          <p className="text-xs opacity-60 mt-0.5">
            {a.format_name} · {a.duration} мин
          </p>
          {!compact && a.patient_phone && (
            <p className="text-xs mt-1 flex items-center gap-1 opacity-50">
              <Phone size={10} /> {a.patient_phone}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5 shrink-0">
          {next && (
            <Button data-testid={`queue-action-${a.id}`}
              size="sm" onClick={() => onTransition(a.id, next.status)}
              className="text-xs gap-1 bg-white/10 hover:bg-white/20 border-0 text-inherit">
              <next.icon size={14} />
              {next.label}
            </Button>
          )}
          {['confirmed', 'waiting'].includes(a.status) && (
            <Button data-testid={`queue-cancel-${a.id}`}
              size="sm" variant="ghost" onClick={() => onCancel(a.id)}
              className="text-xs gap-1 text-red-400/60 hover:text-red-400 hover:bg-red-900/20">
              <X size={12} /> Отмена
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
